import type { Heading } from '@xox/shared'
import { type PointerEvent, useEffect, useRef } from 'react'
import { persisted } from '../lib/storage'
import { useCycles } from '../store/cycles'

export type ControlScheme = 'tap' | 'swipe'
const schemeSchema = {
  safeParse: (v: unknown) =>
    v === 'tap' || v === 'swipe'
      ? { success: true as const, data: v as ControlScheme }
      : { success: false as const },
}
/** The steering scheme this device prefers. */
export const controlScheme = persisted<ControlScheme>('cycles-controls', schemeSchema, 'tap')

const KEYS: Record<string, Heading> = {
  ArrowUp: 0,
  ArrowRight: 1,
  ArrowDown: 2,
  ArrowLeft: 3,
  w: 0,
  d: 1,
  s: 2,
  a: 3,
  W: 0,
  D: 1,
  S: 2,
  A: 3,
}

/** A swipe counts as soon as the finger moved this far; earlier than a tap-release would. */
const SWIPE_MIN_PX = 16

/**
 * Steering: arrows / WASD are absolute headings; on the arena, `tap` turns left or right
 * relative to the cycle's (predicted) heading on pointer down, `swipe` picks the absolute
 * heading from the first movement. Everything fires on the earliest possible event.
 */
export function useCyclesInput(enabled: boolean, scheme: ControlScheme) {
  const pointers = useRef(new Map<number, { x: number; y: number; done: boolean }>())

  useEffect(() => {
    if (!enabled) return
    const onKey = (e: KeyboardEvent) => {
      const h = KEYS[e.key]
      if (h === undefined) return
      const target = e.target as HTMLElement | null
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return
      e.preventDefault()
      useCycles.getState().turn(h)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled])

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!enabled || e.button !== 0) return
    if (scheme === 'tap') {
      const rect = e.currentTarget.getBoundingClientRect()
      useCycles.getState().steer(e.clientX - rect.left < rect.width / 2 ? 'left' : 'right')
      return
    }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY, done: false })
  }
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const p = pointers.current.get(e.pointerId)
    if (!p || p.done || !enabled) return
    const dx = e.clientX - p.x
    const dy = e.clientY - p.y
    if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_MIN_PX) return
    p.done = true
    const h: Heading = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 1 : 3) : dy > 0 ? 2 : 0
    useCycles.getState().turn(h)
  }
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId)
  }
  const onContextMenu = (e: { preventDefault: () => void }) => e.preventDefault()

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onContextMenu }
}
